import React, { useContext, useEffect, useSyncExternalStore } from "react";
import { useNavigate } from "react-router-dom";

import { StateContext } from "../../context/globalContext/context";
import { iGlobalContext } from "../../context/globalContext/interface";
import { toastMessage } from "../../helpers/toast/toastMessage";
import { NudgeStore } from "../../services/intent/NudgeStore";
import { dispatchNudgeCta } from "../../services/intent/dispatchNudgeCta";
import IntentCoachMark from "./IntentCoachMark";
import IntentInlineBanner from "./IntentInlineBanner";
import IntentModal from "./IntentModal";
import SlideInPanel from "./SlideInPanel";

const IntentNudgeRenderer: React.FC = () => {
  const { accessJWT, setChatInputText } = useContext(StateContext) as iGlobalContext;
  const navigate = useNavigate();
  const nudge = useSyncExternalStore(NudgeStore.subscribe, NudgeStore.getSnapshot);

  useEffect(() => {
    console.log(`[intent] renderer mount`);
    if (import.meta.env.MODE === "development") {
      (window as unknown as { __intentNudge: typeof NudgeStore }).__intentNudge = NudgeStore;
    }
    return () => {
      if (import.meta.env.MODE === "development") {
        delete (window as unknown as { __intentNudge?: typeof NudgeStore }).__intentNudge;
      }
    };
  }, []);

  // Pusher connection + channel subscriptions are owned by <PusherBootstrap/>.
  // This renderer just consumes NudgeStore.

  useEffect(() => {
    if (!nudge || nudge.mechanism !== "toast") return;
    const fire =
      nudge.tone === "success"
        ? toastMessage.success
        : nudge.tone === "error"
          ? toastMessage.error
          : toastMessage.info;
    fire(nudge.text);
    NudgeStore.dismiss(nudge.id);
  }, [nudge]);

  if (!nudge) return null;

  const handleDismiss = (): void => NudgeStore.dismiss(nudge.id);
  const handleCta = (): void => {
    if (!nudge.cta) {
      handleDismiss();
      return;
    }
    dispatchNudgeCta(nudge.cta.action, {
      accessJWT,
      navigate,
      setChatInputText,
      traceId: nudge.trace_id,
    })
      .catch(() => {})
      .finally(() => NudgeStore.dismiss(nudge.id));
  };

  switch (nudge.mechanism) {
    case "slide-in":
      return <SlideInPanel nudge={nudge} onDismiss={handleDismiss} onCta={handleCta} />;
    case "modal":
      return <IntentModal nudge={nudge} onDismiss={handleDismiss} onCta={handleCta} />;
    case "inline-banner":
      return <IntentInlineBanner nudge={nudge} onDismiss={handleDismiss} onCta={handleCta} />;
    case "coach-mark":
      return <IntentCoachMark nudge={nudge} onDismiss={handleDismiss} onCta={handleCta} />;
    case "toast":
      return null;
    default:
      console.warn(`[intent] unknown mechanism`, nudge.mechanism);
      return null;
  }
};

export default IntentNudgeRenderer;
